import Moment from 'moment';
import { sumBy, kebabCase } from 'lodash';
import formatDate from "./filters/date/format";
import { getVatOn, unvatify, currency, currencyThousands } from "./filters/financial";
import { formatNumber, unsigned, abs } from "./filters/numeric";
import { placehold } from "./filters/string";
import { minutesToHours } from "./filters/date/minutesToHours";
import { titleCase } from "./filters/string/title-case";
import { formatHandoverDate } from "./filters/date/formatHandoverDate";
import { waitingSince } from "./filters/date/waitingSince";
import { dateDiff } from "./filters/date/dateDiff";
import { humanCurrency } from "./filters/financial/humanCurrency";
import { ucwords } from './filters/string/ucwords';
export const StringFilters = {
    placehold,
    titleCase,
    ucwords,
    kebabCase(value) {
        return kebabCase(value);
    },
    lowerCase(value) {
        return value.toLowerCase();
    },
};
export const FormatFilters = {
    tbc: placehold,
    humanBytes(bytes, decimals = 0) {
        if (bytes == 0)
            return '0 B';
        let k = 1024, sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'], i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i];
    },
    cleanUrl(value) {
        return value.replace('https://', '').replace('http://', '').replace('www.', '');
    },
    domainOnly(value) {
        return value.replace('https://', '').replace('http://', '').replace('www.', '').split('/').shift();
    },
    lastSegment(url) {
        let last = '';
        let segments = url.split('/');
        while (last === '' && segments.length > 0) {
            last = segments.pop()?.trim() ?? '';
        }
        return last;
    },
    append(value, units = null, placeholder = '-') {
        return !parseInt(value) || parseInt(value) <= 0 ? placeholder : value + (units ? ' ' + units : '');
    },
};
export const NumericFilters = {
    formatNumber,
    unsigned,
    abs,
    sum(value, attr) {
        return sumBy(value, attr);
    },
    toTwoDecimalPlaces(value) {
        return Number(value.toFixed(2));
    },
    percentage(value, decimals = 0) {
        return value.toFixed(decimals) + '%';
    },
};
export const FinancialFilters = {
    currency,
    unvatify,
    getVatOn,
    currencyThousands,
    humanCurrency,
};
export const DateFilters = {
    formatDate,
    minutesToHours,
    formatHandoverDate,
    waitingSince,
    dateDiff,
    shortDate(date) {
        return Moment(date).format('D MMM');
    },
    humanDayTime(date, date_format = 'ddd', hour_format = 'h:mma', alternate_format = 'Do MMM') {
        let moment = Moment(date);
        let hourFormat = '[at] h:mma';
        if (moment.hour() === 0 && moment.minute() === 0) {
            hourFormat = '';
        }
        let dayFormat = alternate_format;
        if (Moment().year() !== moment.year()) {
            return moment.format(alternate_format + ' YYYY ' + hourFormat);
        }
        if (Moment().subtract(1, 'd').format('D/M/Y') === moment.format('D/M/Y')) {
            dayFormat = '[Yesterday]';
        }
        else if (Moment().format('D/M/Y') === moment.format('D/M/Y')) {
            dayFormat = '[Today]';
        }
        else if (Moment().add(1, 'd').format('D/M/Y') === moment.format('D/M/Y')) {
            dayFormat = '[Tomorrow]';
        }
        else if (Moment().diff(moment, 'days') < 7) {
            dayFormat = date_format;
        }
        return moment.format(dayFormat + (hourFormat ? ' ' + hourFormat : ''));
    },
    humanDay(date, format = null) {
        if (Moment().subtract(1, 'd').format('D/M/Y') === Moment(date).format('D/M/Y')) {
            return 'Yesterday';
        }
        else if (Moment().format('D/M/Y') === Moment(date).format('D/M/Y')) {
            return 'Today';
        }
        else if (Moment().add(1, 'd').format('D/M/Y') === Moment(date).format('D/M/Y')) {
            return 'Tomorrow';
        }
        if (format) {
            return Moment(date).format(format);
        }
        return Moment(date).format('ddd');
    },
    humanWeek(date) {
        date = Moment(date);
        let weekBeforeLastStart = Moment().hour(0).minute(0).second(0).subtract(14, 'd');
        let lastWeekStart = Moment().hour(0).minute(0).second(0).subtract(7, 'd');
        let nextWeekStart = Moment().hour(0).minute(0).second(0).add(7, 'd');
        let weekAfterNextStart = Moment().hour(0).minute(0).second(0).add(14, 'd');
        let weekAfterNextNextStart = Moment().hour(0).minute(0).second(0).add(21, 'd');
        let now = Moment().hour(0).minute(0).second(0);
        if (date.isBetween(weekBeforeLastStart, lastWeekStart)) {
            return 'Week Before Last';
        }
        else if (date.isBetween(lastWeekStart, now)) {
            return 'Last Week';
        }
        else if (date.isBetween(now, nextWeekStart)) {
            return 'This Week';
        }
        else if (date.isBetween(nextWeekStart, weekAfterNextStart)) {
            return 'Next Week';
        }
        else if (date.isBetween(weekAfterNextStart, weekAfterNextNextStart)) {
            return 'Week After Next';
        }
        return 'Week starting ' + date.format('Do MMMM YYYY');
    },
    relativeDate(date) {
        if (Moment().diff(Moment(date), 'years') >= 1) {
            return Moment(date).format('D MMM YY');
        }
        else if (Moment().diff(Moment(date), 'days') >= 2) {
            return Moment(date).format('D MMM');
        }
        return Moment(date).fromNow();
    },
    longDate(date) {
        if (Moment().format('YYYY') === Moment(date).format('YYYY')) {
            return Moment(date).format('Do MMMM');
        }
        return Moment(date).format('Do MMMM YYYY');
    },
    year(date) {
        return Moment(date).format('Y');
    },
    formatTime(date) {
        return Moment(date).format('HH:mm:ss');
    },
    humanDiff(from, to, tense = true) {
        if (!to) {
            to = Moment();
        }
        return Moment(from).to(to, tense);
    },
};
export const ContactFormatFilters = {
    formatContactName(contact) {
        if (contact.title && contact.first_name && contact.last_name) {
            return [contact.title, contact.first_name, contact.last_name].join(' ');
        }
        else if (contact.company_name) {
            return contact.company_name;
        }
        return null;
    },
    formatContactAddress(contact, separator = '<br>') {
        let str = '';
        let props = ['address_1', 'address_2', 'address_3', 'town', 'county'];
        for (let i in props) {
            if (contact[props[i]] && contact[props[i]] !== '') {
                str += contact[props[i]] + separator;
            }
        }
        str += contact.post_code + ', ' + contact.country;
        return str;
    }
};
export default {
    ...StringFilters,
    ...FormatFilters,
    ...NumericFilters,
    ...FinancialFilters,
    ...DateFilters,
    ...ContactFormatFilters
};
